import { NativeModule, registerWebModule } from 'expo'

import { GeeTest, GeetestCaptchaResult, GeetestModuleEvents } from './GeetestModule.types'

const GT4_SCRIPT_URL = 'https://gd-assets.jinshujucdn.com/geetest.4.1.0.js'

export type CaptchaVerifyResult = {
  success: boolean
  result?: GeetestCaptchaResult
  error?: string
}

let scriptLoadPromise: Promise<void> | null = null

function loadScript(): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('GeeTest is only available in browser'))
  }
  if (window.initGeetest4) return Promise.resolve()
  if (scriptLoadPromise) return scriptLoadPromise
  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = GT4_SCRIPT_URL
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load GeeTest script'))
    document.head.appendChild(script)
  })
  return scriptLoadPromise
}

class GeetestModule extends NativeModule<GeetestModuleEvents> {

  async verify(captchaID: string): Promise<CaptchaVerifyResult> {
    try {
      await loadScript()
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Failed to load GeeTest' }
    }

    const initGeetest4 = window.initGeetest4
    if (!initGeetest4) {
      return { success: false, error: 'GeeTest script not loaded' }
    }

    return new Promise(resolve => {
      let settled = false
      const done = (outcome: CaptchaVerifyResult) => {
        if (!settled) {
          settled = true
          resolve(outcome)
        }
      }
      initGeetest4(
        { captchaId: captchaID, product: 'bind', language: 'zh-CN' },
        (captchaObj: GeeTest) => {
          captchaObj.onReady(() => {
            captchaObj.showCaptcha()
          })
          captchaObj.onSuccess(() => {
            const v = captchaObj.getValidate()
            done({
              success: true,
              result: {
                pass_token: v.pass_token,
                lot_number: v.lot_number,
                gen_time: v.gen_time,
                captcha_output: v.captcha_output,
                captcha_id: captchaID,
              },
            })
          })
          captchaObj.onError(error => {
            done({ success: false, error: error?.msg ?? 'Verification failed' })
          })
          if (captchaObj.onClose) {
            captchaObj.onClose(() => done({ success: false, error: 'cancelled' }))
          }
        }
      )
    })
  }

  cancel(): void {
    // No-op on web
  }
}

export default registerWebModule(GeetestModule, 'GeetestModule')
